import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ServerPath } from '../../Constant';
import { _axios } from '../../utils/axios';

const EditMultiyatraBelt = ({ show, onHide, templateData }) => {
    const [formData, setFormData] = useState({
        category: '',
        status: '',
        action_button_label: '',
        priority: '',
        action_link: '',
        display_start_date: '',
        display_end_date: '',
        display_time: '',
        frequency: '',
        image_only: 0
    });

    // Language selection
    const [selectedLanguages, setSelectedLanguages] = useState({
        english: false,
        hindi: false,
        marathi: false,
        gujarati: false
    });

    // Display sections
    const [displaySections, setDisplaySections] = useState({
        home: false,
        wisdom: false,
        parikrama: false,
        teertha: false,
        devotion: false
    });

    // Language-specific data
    const [languageData, setLanguageData] = useState({
        english: { title: '', blessing_text: '', image: null, imagePreview: null },
        hindi: { title: '', blessing_text: '', image: null, imagePreview: null },
        marathi: { title: '', blessing_text: '', image: null, imagePreview: null },
        gujarati: { title: '', blessing_text: '', image: null, imagePreview: null }
    });

    const [isLoading, setIsLoading] = useState(false);
    const emojiPattern = /[\p{Emoji}]/u;

    const categoryOptions = [
        'Daily Blessing',
        'Festival',
        'Quote',
        'Promotion',
        'Live Aarti',
        'Ad'
    ];

    const statusOptions = [
        'Active',
        'Archive'
    ];

    const frequencyOptions = [
        'Once',
        'Daily',
        'Weekly',
        'Monthly',
        'Quarterly',
        'Half-Yearly',
        'Annually'
    ];

    // Initialize form data when component receives templateData
    useEffect(() => {
        if (templateData) {
            const formatDateForInput = (dateString) => {
                if (!dateString) return '';

                // Backend returns plain local string: "YYYY-MM-DD HH:mm:ss" — slice date part directly, no Date parsing
                return dateString.slice(0, 10);
            };

            console.log('[EditKuberBelt] raw from API — display_start_date:', templateData.display_start_date, '| display_end_date:', templateData.display_end_date);
            setFormData({
                category: templateData.category || '',
                status: templateData.status || '',
                action_button_label: templateData.action_button_label || '',
                priority: templateData.priority || '',
                action_link: templateData.action_link || '',
                display_start_date: formatDateForInput(templateData.display_start_date),
                display_end_date: formatDateForInput(templateData.display_end_date),
                display_time: templateData.display_time || '',
                frequency: templateData.frequency || '',
                image_only: templateData.image_only || 0
            });

            // Parse display sections if available
            if (templateData.display_section) {
                try {
                    const sections = typeof templateData.display_section === 'string' 
                        ? JSON.parse(templateData.display_section)
                        : templateData.display_section;
                    setDisplaySections(sections);
                } catch (e) {
                    console.error('Error parsing display sections:', e);
                }
            }

            // Set language data
            const langs = {
                english: { title: templateData.title || '', blessing_text: templateData.blessing_text || '', imagePreview: templateData.thumbnail_url || null },
                hindi: { title: templateData.title_hin || '', blessing_text: templateData.blessing_text_hin || '', imagePreview: templateData.thumbnail_url_hin || null },
                marathi: { title: templateData.title_mar || '', blessing_text: templateData.blessing_text_mar || '', imagePreview: templateData.thumbnail_url_mar || null },
                gujarati: { title: templateData.title_guj || '', blessing_text: templateData.blessing_text_guj || '', imagePreview: templateData.thumbnail_url_guj || null }
            };

            setLanguageData(langs);

            // Set selected languages based on which have data
            setSelectedLanguages({
                english: !!(templateData.title || templateData.thumbnail_url),
                hindi: !!(templateData.title_hin || templateData.thumbnail_url_hin),
                marathi: !!(templateData.title_mar || templateData.thumbnail_url_mar),
                gujarati: !!(templateData.title_guj || templateData.thumbnail_url_guj)
            });
        }
    }, [templateData]);

    const handleLanguageChange = (lang) => {
        setSelectedLanguages(prev => ({
            ...prev,
            [lang]: !prev[lang]
        }));
    };

    const handleDisplaySectionChange = (section) => {
        setDisplaySections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const handleLanguageInputChange = (lang, field, value) => {
        if ((field === 'title' || field === 'blessing_text')) {
            if (emojiPattern.test(value) && !emojiPattern.test(languageData[lang][field])) {
                toast.error('Emojis are not allowed in text fields');
                return;
            }
        }

        setLanguageData(prev => ({
            ...prev,
            [lang]: {
                ...prev[lang],
                [field]: value
            }
        }));
    };

    const handleLanguageImageChange = (lang, e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 3 * 1024 * 1024) {
                toast.error('Image size should be less than 3MB');
                return;
            }

            const fileType = file.type.toLowerCase();
            if (!fileType.includes('jpeg') && !fileType.includes('jpg') && !fileType.includes('png') && !fileType.includes('webp')) {
                toast.error('Only JPG/PNG/WEBP files are allowed');
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setLanguageData(prev => ({
                    ...prev,
                    [lang]: {
                        ...prev[lang],
                        image: file,
                        imagePreview: reader.result
                    }
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleChange = (e) => {
        const { name, value, checked } = e.target;

        if (name === 'category' && value === 'Live Aarti') {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                display_time: prev.display_time || new Date().toTimeString().slice(0, 5)
            }));
        } else if (name === 'category' && value !== 'Live Aarti') {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                display_time: ''
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: name === 'image_only' ? (checked ? 1 : 0) : value
            }));
        }

        if (name === 'display_start_date' || name === 'display_end_date') {
            const startDate = name === 'display_start_date' ? value : formData.display_start_date;
            const endDate = name === 'display_end_date' ? value : formData.display_end_date;

            if (startDate && endDate) {
                const start = new Date(startDate);
                const end = new Date(endDate);

                if (end < start) {
                    toast.error('End date cannot be earlier than start date');
                    return;
                }
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isLoading) return;

        try {
            setIsLoading(true);

            // Validate at least one language is selected
            const hasLanguageSelected = Object.values(selectedLanguages).some(val => val);
            if (!hasLanguageSelected) {
                toast.error('Please select at least one language');
                setIsLoading(false);
                return;
            }

            // Validate at least one display section is selected
            const hasDisplaySection = Object.values(displaySections).some(val => val);
            if (!hasDisplaySection) {
                toast.error('Please select at least one display section');
                setIsLoading(false);
                return;
            }

            // Validate required fields
            const requiredFields = [
                { name: 'category', label: 'Category' },
                { name: 'priority', label: 'Priority' },
                { name: 'display_start_date', label: 'Display Start Date' },
                { name: 'frequency', label: 'Frequency' },
                { name: 'action_link', label: 'Action link/URL' }
            ];

            for (const field of requiredFields) {
                if (!formData[field.name]) {
                    toast.error(`Please enter ${field.label}`);
                    setIsLoading(false);
                    return;
                }
            }

            // Validate language-specific data for selected languages
            for (const [lang, isSelected] of Object.entries(selectedLanguages)) {
                if (isSelected) {
                    if (formData.image_only === 0) {
                        if (!languageData[lang].title) {
                            toast.error(`Please enter title for ${lang.charAt(0).toUpperCase() + lang.slice(1)}`);
                            setIsLoading(false);
                            return;
                        }
                        if (!languageData[lang].blessing_text) {
                            toast.error(`Please enter blessing text for ${lang.charAt(0).toUpperCase() + lang.slice(1)}`);
                            setIsLoading(false);
                            return;
                        }
                        if (languageData[lang].title.length > 30) {
                            toast.error(`Title for ${lang} must be 30 characters or less`);
                            setIsLoading(false);
                            return;
                        }
                        if (languageData[lang].blessing_text.length > 50) {
                            toast.error(`Blessing Text for ${lang} must be 50 characters or less`);
                            setIsLoading(false);
                            return;
                        }
                    }
                }
            }

            if (formData.category === 'Live Aarti' && !formData.display_time) {
                toast.error('Time is required for Live Aarti category');
                setIsLoading(false);
                return;
            }

            if (formData.display_start_date && formData.display_end_date) {
                const startDate = new Date(formData.display_start_date);
                const endDate = new Date(formData.display_end_date);

                if (endDate < startDate) {
                    toast.error('End date cannot be earlier than start date');
                    setIsLoading(false);
                    return;
                }
            }

            // Prepare update data
            console.log('[EditKuberBelt] display_start_date:', formData.display_start_date, '| display_end_date:', formData.display_end_date ? `${formData.display_end_date} 23:59:59` : null);
            const updateData = {
                category: formData.category,
                status: formData.status,
                action_button_label: formData.action_button_label || null,
                priority: formData.priority,
                action_link: formData.action_link || null,
                display_start_date: formData.display_start_date,
                display_end_date: formData.display_end_date ? `${formData.display_end_date} 23:59:59` : null,
                display_time: formData.display_time || null,
                frequency: formData.frequency,
                image_only: formData.image_only,
                display_section: JSON.stringify(displaySections)
            };

            // Add language-specific data
            const langMap = {
                english: { title: 'title', blessing: 'blessing_text', thumbUrl: 'thumbnail_url', thumbContent: 'thumbnailContent', thumbType: 'thumbnailType' },
                hindi: { title: 'title_hin', blessing: 'blessing_text_hin', thumbUrl: 'thumbnail_url_hin', thumbContent: 'thumbnailContent_hin', thumbType: 'thumbnailType_hin' },
                marathi: { title: 'title_mar', blessing: 'blessing_text_mar', thumbUrl: 'thumbnail_url_mar', thumbContent: 'thumbnailContent_mar', thumbType: 'thumbnailType_mar' },
                gujarati: { title: 'title_guj', blessing: 'blessing_text_guj', thumbUrl: 'thumbnail_url_guj', thumbContent: 'thumbnailContent_guj', thumbType: 'thumbnailType_guj' }
            };

            // Process each language
            const imagePromises = [];

            for (const [lang, isSelected] of Object.entries(selectedLanguages)) {
                const mapping = langMap[lang];

                if (isSelected) {
                    updateData[mapping.title] = languageData[lang].title;
                    updateData[mapping.blessing] = languageData[lang].blessing_text;

                    if (languageData[lang].image) {
                        // New image selected → send as base64 with mime type
                        const promise = new Promise((resolve, reject) => {
                            const reader = new FileReader();
                            reader.readAsDataURL(languageData[lang].image);
                            reader.onloadend = () => {
                                const base64Data = reader.result.split(',')[1];
                                updateData[mapping.thumbContent] = base64Data;
                                updateData[mapping.thumbType] = languageData[lang].image.type;
                                resolve();
                            };
                            reader.onerror = (error) => reject(error);
                        });
                        imagePromises.push(promise);
                    } else if (languageData[lang].imagePreview) {
                        // Existing URL unchanged → send it using the same field name as the GET API
                        updateData[mapping.thumbUrl] = languageData[lang].imagePreview;
                    }
                } else {
                    // Language unchecked → null out all fields
                    updateData[mapping.title] = null;
                    updateData[mapping.blessing] = null;
                    updateData[mapping.thumbUrl] = null;
                    updateData[mapping.thumbContent] = null;
                    updateData[mapping.thumbType] = null;
                }
            }

            // Wait for all image processing to complete
            await Promise.all(imagePromises);

            console.log("Submitting update data:", updateData);

            const response = await _axios.put(`${ServerPath}/kuber-belt/${templateData.template_id}`, updateData);

            if (response.data.success) {
                toast.success('Template updated successfully');
                setTimeout(() => {
                    onHide();
                    window.location.reload();
                }, 1500);
            } else {
                toast.error(response.data.message || 'Failed to update template');
                setIsLoading(false);
            }

        } catch (error) {
            console.error('Error updating template:', error);
            toast.error(error.response?.data?.message || error.message || 'Failed to update template');
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            category: '',
            status: '',
            action_button_label: '',
            priority: '',
            action_link: '',
            display_start_date: '',
            display_end_date: '',
            display_time: '',
            frequency: '',
            image_only: 0
        });
        setSelectedLanguages({
            english: false,
            hindi: false,
            marathi: false,
            gujarati: false
        });
        setDisplaySections({
            home: false,
            wisdom: false,
            parikrama: false,
            teertha: false,
            devotion: false
        });
        setLanguageData({
            english: { title: '', blessing_text: '', image: null, imagePreview: null },
            hindi: { title: '', blessing_text: '', image: null, imagePreview: null },
            marathi: { title: '', blessing_text: '', image: null, imagePreview: null },
            gujarati: { title: '', blessing_text: '', image: null, imagePreview: null }
        });
        setIsLoading(false);
        onHide();
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            size="xl"
            centered
            style={{ borderRadius: '10px' }}
        >
            <div style={{ borderRadius: '10px' }}>
                <Modal.Header closeButton style={{ borderBottom: '1px solid #E2E8F0', padding: '20px 24px' }}>
                    <Modal.Title style={{ fontWeight: '600', fontSize: '22px' }}>Edit Template</Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ padding: '36px' }}>
                    <Form>
                        {/* Image Only Checkbox */}
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Check
                                    type="checkbox"
                                    id="edit-image-only"
                                    label="Image Only"
                                    name="image_only"
                                    checked={formData.image_only === 1}
                                    onChange={handleChange}
                                    style={{
                                        fontSize: '18px',
                                        fontWeight: '600',
                                        color: '#4A5568'
                                    }}
                                />
                            </Col>
                        </Row>

                        {/* All Titles in One Row */}
                        {formData.image_only === 0 && (
                            <Row className="mb-3">
                                {selectedLanguages.english && (
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: '#4A5568', fontWeight: '500', fontSize: '14px' }}>
                                                English Title <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={languageData.english.title}
                                                onChange={(e) => handleLanguageInputChange('english', 'title', e.target.value)}
                                                placeholder="English title"
                                                maxLength={30}
                                                style={{
                                                    height: '46px',
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: '6px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                            <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                                {languageData.english.title.length}/30
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                )}
                                {selectedLanguages.hindi && (
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: '#4A5568', fontWeight: '500', fontSize: '14px' }}>
                                                Hindi Title <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={languageData.hindi.title}
                                                onChange={(e) => handleLanguageInputChange('hindi', 'title', e.target.value)}
                                                placeholder="Hindi title"
                                                maxLength={30}
                                                style={{
                                                    height: '46px',
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: '6px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                            <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                                {languageData.hindi.title.length}/30
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                )}
                                {selectedLanguages.marathi && (
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: '#4A5568', fontWeight: '500', fontSize: '14px' }}>
                                                Marathi Title <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={languageData.marathi.title}
                                                onChange={(e) => handleLanguageInputChange('marathi', 'title', e.target.value)}
                                                placeholder="Marathi title"
                                                maxLength={30}
                                                style={{
                                                    height: '46px',
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: '6px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                            <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                                {languageData.marathi.title.length}/30
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                )}
                                {selectedLanguages.gujarati && (
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: '#4A5568', fontWeight: '500', fontSize: '14px' }}>
                                                Gujarati Title <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={languageData.gujarati.title}
                                                onChange={(e) => handleLanguageInputChange('gujarati', 'title', e.target.value)}
                                                placeholder="Gujarati title"
                                                maxLength={30}
                                                style={{
                                                    height: '46px',
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: '6px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                            <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                                {languageData.gujarati.title.length}/30
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                )}
                            </Row>
                        )}

                        {/* All Blessing Texts in One Row */}
                        {formData.image_only === 0 && (
                            <Row className="mb-3">
                                {selectedLanguages.english && (
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: '#4A5568', fontWeight: '500', fontSize: '14px' }}>
                                                English Blessing Text <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                value={languageData.english.blessing_text}
                                                onChange={(e) => handleLanguageInputChange('english', 'blessing_text', e.target.value)}
                                                placeholder="English blessing text"
                                                maxLength={50}
                                                style={{
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: '6px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                            <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                                {languageData.english.blessing_text.length}/50
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                )}
                                {selectedLanguages.hindi && (
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: '#4A5568', fontWeight: '500', fontSize: '14px' }}>
                                                Hindi Blessing Text <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                value={languageData.hindi.blessing_text}
                                                onChange={(e) => handleLanguageInputChange('hindi', 'blessing_text', e.target.value)}
                                                placeholder="Hindi blessing text"
                                                maxLength={50}
                                                style={{
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: '6px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                            <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                                {languageData.hindi.blessing_text.length}/50
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                )}
                                {selectedLanguages.marathi && (
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: '#4A5568', fontWeight: '500', fontSize: '14px' }}>
                                                Marathi Blessing Text <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                value={languageData.marathi.blessing_text}
                                                onChange={(e) => handleLanguageInputChange('marathi', 'blessing_text', e.target.value)}
                                                placeholder="Marathi blessing text"
                                                maxLength={50}
                                                style={{
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: '6px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                            <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                                {languageData.marathi.blessing_text.length}/50
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                )}
                                {selectedLanguages.gujarati && (
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: '#4A5568', fontWeight: '500', fontSize: '14px' }}>
                                                Gujarati Blessing Text <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                value={languageData.gujarati.blessing_text}
                                                onChange={(e) => handleLanguageInputChange('gujarati', 'blessing_text', e.target.value)}
                                                placeholder="Gujarati blessing text"
                                                maxLength={50}
                                                style={{
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: '6px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                            <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                                {languageData.gujarati.blessing_text.length}/50
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                )}
                            </Row>
                        )}

                        {/* Select Languages, Category and Status in One Row */}
                        <Row className="mb-3" style={{ alignItems: 'flex-start' }}>
                            <Col md={6}>
                                <Form.Label style={{ color: '#4A5568', fontWeight: '600', fontSize: '16px', marginBottom: '8px' }}>
                                    Select Languages <span className="text-danger">*</span>
                                </Form.Label>
                                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                    <Form.Check
                                        type="checkbox"
                                        id="edit-lang-english"
                                        label="English"
                                        checked={selectedLanguages.english}
                                        onChange={() => handleLanguageChange('english')}
                                        style={{ fontSize: '14px' }}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        id="edit-lang-hindi"
                                        label="Hindi"
                                        checked={selectedLanguages.hindi}
                                        onChange={() => handleLanguageChange('hindi')}
                                        style={{ fontSize: '14px' }}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        id="edit-lang-marathi"
                                        label="Marathi"
                                        checked={selectedLanguages.marathi}
                                        onChange={() => handleLanguageChange('marathi')}
                                        style={{ fontSize: '14px' }}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        id="edit-lang-gujarati"
                                        label="Gujarati"
                                        checked={selectedLanguages.gujarati}
                                        onChange={() => handleLanguageChange('gujarati')}
                                        style={{ fontSize: '14px' }}
                                    />
                                </div>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="mb-2" style={{ color: '#4A5568', fontWeight: '500', fontSize: '16px' }}>
                                        Category <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        name="category"
                                        value={formData.category}
                                        onChange={handleChange}
                                        style={{
                                            height: '46px',
                                            border: '1px solid #E2E8F0',
                                            backgroundColor: '#FFFFFF',
                                            borderRadius: '6px'
                                        }}
                                    >
                                        <option value="" disabled>Select category</option>
                                        {categoryOptions.map((option, index) => (
                                            <option key={index} value={option}>{option}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="mb-2" style={{ color: '#4A5568', fontWeight: '500', fontSize: '16px' }}>
                                        Status <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleChange}
                                        style={{
                                            height: '46px',
                                            border: '1px solid #E2E8F0',
                                            backgroundColor: '#FFFFFF',
                                            borderRadius: '6px'
                                        }}
                                    >
                                        <option value="" disabled>Select status</option>
                                        {statusOptions.map((option, index) => (
                                            <option key={index} value={option}>{option}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* All Banners in One Row */}
                        <Row className="mb-3">
                            {selectedLanguages.english && (
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label style={{ color: '#4A5568', fontWeight: '500', fontSize: '14px' }}>
                                            English Banner <span className="text-danger">*</span>
                                        </Form.Label>
                                        <div style={{
                                            border: '2px dashed #CBD5E0',
                                            borderRadius: '8px',
                                            padding: '16px',
                                            textAlign: 'center',
                                            backgroundColor: '#FFFFFF',
                                            cursor: 'pointer',
                                            minHeight: '120px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                            onClick={() => document.getElementById('edit-english-image-upload').click()}
                                        >
                                            {languageData.english.imagePreview ? (
                                                <img 
                                                    src={languageData.english.imagePreview} 
                                                    alt="Preview" 
                                                    style={{ maxWidth: '100%', maxHeight: '100px', borderRadius: '4px' }}
                                                />
                                            ) : (
                                                <>
                                                    <div style={{ fontSize: '32px', color: '#CBD5E0', marginBottom: '8px' }}>+</div>
                                                    <p style={{ color: '#718096', margin: 0, fontSize: '12px' }}>Upload</p>
                                                </>
                                            )}
                                        </div>
                                        <Form.Control
                                            type="file"
                                            id="edit-english-image-upload"
                                            onChange={(e) => handleLanguageImageChange('english', e)}
                                            accept="image/jpeg,image/jpg,image/png,image/webp"
                                            style={{ display: 'none' }}
                                        />
                                    </Form.Group>
                                </Col>
                            )}
                            {selectedLanguages.hindi && (
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label style={{ color: '#4A5568', fontWeight: '500', fontSize: '14px' }}>
                                            Hindi Banner <span className="text-danger">*</span>
                                        </Form.Label>
                                        <div style={{
                                            border: '2px dashed #CBD5E0',
                                            borderRadius: '8px',
                                            padding: '16px',
                                            textAlign: 'center',
                                            backgroundColor: '#FFFFFF',
                                            cursor: 'pointer',
                                            minHeight: '120px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                            onClick={() => document.getElementById('edit-hindi-image-upload').click()}
                                        >
                                            {languageData.hindi.imagePreview ? (
                                                <img 
                                                    src={languageData.hindi.imagePreview} 
                                                    alt="Preview" 
                                                    style={{ maxWidth: '100%', maxHeight: '100px', borderRadius: '4px' }}
                                                />
                                            ) : (
                                                <>
                                                    <div style={{ fontSize: '32px', color: '#CBD5E0', marginBottom: '8px' }}>+</div>
                                                    <p style={{ color: '#718096', margin: 0, fontSize: '12px' }}>Upload</p>
                                                </>
                                            )}
                                        </div>
                                        <Form.Control
                                            type="file"
                                            id="edit-hindi-image-upload"
                                            onChange={(e) => handleLanguageImageChange('hindi', e)}
                                            accept="image/jpeg,image/jpg,image/png,image/webp"
                                            style={{ display: 'none' }}
                                        />
                                    </Form.Group>
                                </Col>
                            )}
                            {selectedLanguages.marathi && (
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label style={{ color: '#4A5568', fontWeight: '500', fontSize: '14px' }}>
                                            Marathi Banner <span className="text-danger">*</span>
                                        </Form.Label>
                                        <div style={{
                                            border: '2px dashed #CBD5E0',
                                            borderRadius: '8px',
                                            padding: '16px',
                                            textAlign: 'center',
                                            backgroundColor: '#FFFFFF',
                                            cursor: 'pointer',
                                            minHeight: '120px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                            onClick={() => document.getElementById('edit-marathi-image-upload').click()}
                                        >
                                            {languageData.marathi.imagePreview ? (
                                                <img 
                                                    src={languageData.marathi.imagePreview} 
                                                    alt="Preview" 
                                                    style={{ maxWidth: '100%', maxHeight: '100px', borderRadius: '4px' }}
                                                />
                                            ) : (
                                                <>
                                                    <div style={{ fontSize: '32px', color: '#CBD5E0', marginBottom: '8px' }}>+</div>
                                                    <p style={{ color: '#718096', margin: 0, fontSize: '12px' }}>Upload</p>
                                                </>
                                            )}
                                        </div>
                                        <Form.Control
                                            type="file"
                                            id="edit-marathi-image-upload"
                                            onChange={(e) => handleLanguageImageChange('marathi', e)}
                                            accept="image/jpeg,image/jpg,image/png,image/webp"
                                            style={{ display: 'none' }}
                                        />
                                    </Form.Group>
                                </Col>
                            )}
                            {selectedLanguages.gujarati && (
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label style={{ color: '#4A5568', fontWeight: '500', fontSize: '14px' }}>
                                            Gujarati Banner <span className="text-danger">*</span>
                                        </Form.Label>
                                        <div style={{
                                            border: '2px dashed #CBD5E0',
                                            borderRadius: '8px',
                                            padding: '16px',
                                            textAlign: 'center',
                                            backgroundColor: '#FFFFFF',
                                            cursor: 'pointer',
                                            minHeight: '120px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                            onClick={() => document.getElementById('edit-gujarati-image-upload').click()}
                                        >
                                            {languageData.gujarati.imagePreview ? (
                                                <img 
                                                    src={languageData.gujarati.imagePreview} 
                                                    alt="Preview" 
                                                    style={{ maxWidth: '100%', maxHeight: '100px', borderRadius: '4px' }}
                                                />
                                            ) : (
                                                <>
                                                    <div style={{ fontSize: '32px', color: '#CBD5E0', marginBottom: '8px' }}>+</div>
                                                    <p style={{ color: '#718096', margin: 0, fontSize: '12px' }}>Upload</p>
                                                </>
                                            )}
                                        </div>
                                        <Form.Control
                                            type="file"
                                            id="edit-gujarati-image-upload"
                                            onChange={(e) => handleLanguageImageChange('gujarati', e)}
                                            accept="image/jpeg,image/jpg,image/png,image/webp"
                                            style={{ display: 'none' }}
                                        />
                                    </Form.Group>
                                </Col>
                            )}
                        </Row>

                        {/* Display Section in One Row */}
                        <Row className="mb-3">
                            <Col md={12}>
                                <Form.Label style={{ color: '#4A5568', fontWeight: '600', fontSize: '16px', marginBottom: '8px' }}>
                                    Display Section <span className="text-danger">*</span>
                                </Form.Label>
                                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                                    <Form.Check
                                        type="checkbox"
                                        id="edit-section-home"
                                        label="Home"
                                        checked={displaySections.home}
                                        onChange={() => handleDisplaySectionChange('home')}
                                        style={{ fontSize: '14px' }}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        id="edit-section-wisdom"
                                        label="Wisdom"
                                        checked={displaySections.wisdom}
                                        onChange={() => handleDisplaySectionChange('wisdom')}
                                        style={{ fontSize: '14px' }}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        id="edit-section-parikrama"
                                        label="Parikrama"
                                        checked={displaySections.parikrama}
                                        onChange={() => handleDisplaySectionChange('parikrama')}
                                        style={{ fontSize: '14px' }}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        id="edit-section-teertha"
                                        label="Teertha"
                                        checked={displaySections.teertha}
                                        onChange={() => handleDisplaySectionChange('teertha')}
                                        style={{ fontSize: '14px' }}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        id="edit-section-devotion"
                                        label="Devotion"
                                        checked={displaySections.devotion}
                                        onChange={() => handleDisplaySectionChange('devotion')}
                                        style={{ fontSize: '14px' }}
                                    />
                                </div>
                            </Col>
                        </Row>

                        {/* Action Link and Priority Row */}
                        <Row className="mb-3">
                            <Col md={9}>
                                <Form.Group>
                                    <Form.Label className="mb-2" style={{ color: '#4A5568', fontWeight: '500', fontSize: '16px' }}>
                                        Action link/URL <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="action_link"
                                        value={formData.action_link}
                                        onChange={handleChange}
                                        placeholder="Enter Action link/URL"
                                        style={{
                                            height: '46px',
                                            border: '1px solid #E2E8F0',
                                            backgroundColor: '#FFFFFF',
                                            borderRadius: '6px'
                                        }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="mb-2" style={{ color: '#4A5568', fontWeight: '500', fontSize: '16px' }}>
                                        Priority <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="priority"
                                        value={formData.priority}
                                        onChange={handleChange}
                                        placeholder="Enter Priority"
                                        min="1"
                                        max="10"
                                        style={{
                                            height: '46px',
                                            border: '1px solid #E2E8F0',
                                            backgroundColor: '#FFFFFF',
                                            borderRadius: '6px'
                                        }}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Date Fields Row */}
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="mb-2" style={{ color: '#4A5568', fontWeight: '500', fontSize: '16px' }}>
                                        Display Start Date <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="display_start_date"
                                        value={formData.display_start_date}
                                        onChange={handleChange}
                                        style={{
                                            height: '46px',
                                            border: '1px solid #E2E8F0',
                                            backgroundColor: '#FFFFFF',
                                            borderRadius: '6px'
                                        }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="mb-2" style={{ color: '#4A5568', fontWeight: '500', fontSize: '16px' }}>
                                        Display End Date
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="display_end_date"
                                        value={formData.display_end_date}
                                        onChange={handleChange}
                                        style={{
                                            height: '46px',
                                            border: '1px solid #E2E8F0',
                                            backgroundColor: '#FFFFFF',
                                            borderRadius: '6px'
                                        }}
                                    />
                                </Form.Group>
                            </Col>
                            {formData.category === 'Live Aarti' ? (
                                <>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="mb-2" style={{ color: '#4A5568', fontWeight: '500', fontSize: '16px' }}>
                                                Time <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="time"
                                                name="display_time"
                                                value={formData.display_time}
                                                onChange={handleChange}
                                                style={{
                                                    height: '46px',
                                                    border: '1px solid #E2E8F0',
                                                    backgroundColor: '#FFFFFF',
                                                    borderRadius: '6px'
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="mb-2" style={{ color: '#4A5568', fontWeight: '500', fontSize: '16px' }}>
                                                Frequency <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                name="frequency"
                                                value={formData.frequency}
                                                onChange={handleChange}
                                                style={{
                                                    height: '46px',
                                                    border: '1px solid #E2E8F0',
                                                    backgroundColor: '#FFFFFF',
                                                    borderRadius: '6px'
                                                }}
                                            >
                                                <option value="" disabled>Select frequency</option>
                                                {frequencyOptions.map((option, index) => (
                                                    <option key={index} value={option}>{option}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </>
                            ) : (
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="mb-2" style={{ color: '#4A5568', fontWeight: '500', fontSize: '16px' }}>
                                            Frequency <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Form.Select
                                            name="frequency"
                                            value={formData.frequency}
                                            onChange={handleChange}
                                            style={{
                                                height: '46px',
                                                border: '1px solid #E2E8F0',
                                                backgroundColor: '#FFFFFF',
                                                borderRadius: '6px'
                                            }}
                                        >
                                            <option value="" disabled>Select frequency</option>
                                            {frequencyOptions.map((option, index) => (
                                                <option key={index} value={option}>{option}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            )}
                        </Row>
                    </Form>
                </Modal.Body>

                <div style={{ padding: '0 24px 24px 0', display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={isLoading}
                        style={{
                            backgroundColor: '#5662F6',
                            borderColor: '#5662F6',
                            borderRadius: '6px',
                            padding: '10px 40px',
                            fontWeight: '500',
                            fontSize: '16px'
                        }}
                    >
                        {isLoading ? 'Updating...' : 'Update'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default EditMultiyatraBelt;
